import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Paper,
    Stack,
    Typography,
} from '@mui/material'
import { green, orange, red } from '@mui/material/colors'
import React, { useState } from 'react'
import { useAppDispatch } from '../../hooks/reduxHooks'
import { EventManager } from '../../services/api/EventManager'
import {
    ApprovedEventsSheetSyncEventOutcome,
    ApprovedEventsSheetSyncEventResultDto,
    ForceApprovedEventsSheetSyncResultDto,
} from '../../services/api/collections/event/forceApprovedEventsSheetSync/forceApprovedEventsSheetSync_200'
import { displayMessage } from '../../services/redux/snackbar/snackbar-actions'

interface ForceApprovedEventsSheetSyncDialogProps {
    open: boolean
    onClose: () => void
}

type DialogPhase = 'idle' | 'loading' | 'done'

const formatDate = (iso: string) => new Date(iso).toLocaleDateString('cs-CZ')

const renderOutcomeChip = (outcome: ApprovedEventsSheetSyncEventOutcome): { label: string; color: string } => {
    switch (outcome) {
        case ApprovedEventsSheetSyncEventOutcome.Created:
            return { label: 'Vytvořeno', color: green[800] }
        case ApprovedEventsSheetSyncEventOutcome.Updated:
            return { label: 'Aktualizováno', color: green[600] }
        case ApprovedEventsSheetSyncEventOutcome.Moved:
            return { label: 'Přesunuto', color: orange[700] }
        case ApprovedEventsSheetSyncEventOutcome.Skipped:
            return { label: 'Přeskočeno', color: 'text.secondary' }
        case ApprovedEventsSheetSyncEventOutcome.Failed:
            return { label: 'Chyba', color: red[800] }
    }
}

const ForceApprovedEventsSheetSyncDialog: React.FC<ForceApprovedEventsSheetSyncDialogProps> = ({ open, onClose }) => {
    const dispatch = useAppDispatch()
    const [phase, setPhase] = useState<DialogPhase>('idle')
    const [response, setResponse] = useState<ForceApprovedEventsSheetSyncResultDto | null>(null)

    const reset = () => {
        setPhase('idle')
        setResponse(null)
    }

    const handleClose = () => {
        if (phase === 'loading') return
        reset()
        onClose()
    }

    const handleRun = async () => {
        setPhase('loading')
        try {
            const result = await EventManager.getManager().forceApprovedEventsSheetSyncAsync()
            setResponse(result)
            setPhase('done')
        } catch (error) {
            dispatch(displayMessage({ message: error, type: 'error' }))
            setPhase('idle')
        }
    }

    const failedResults = response?.eventResults.filter((r) => r.outcome === ApprovedEventsSheetSyncEventOutcome.Failed) ?? []

    // Idle and error states need room for help text / error rows; the plain success
    // summary is just five chips — md would leave a lot of empty whitespace.
    const isCompactSummary =
        phase === 'done' &&
        response != null &&
        !response.alreadyRunning &&
        !response.spreadsheetNotConfigured &&
        failedResults.length === 0

    return (
        <Dialog open={open} onClose={handleClose} maxWidth={isCompactSummary ? 'sm' : 'md'} fullWidth>
            <DialogTitle>Aktualizovat rozpis akcí</DialogTitle>
            <DialogContent sx={{ pt: 2 }}>
                {phase === 'idle' && (
                    <Stack spacing={2}>
                        <Alert severity='info' variant='filled'>
                            Tato akce projde všechny schválené a dokončené akce v nejbližších 90 dnech a doplní je do sdíleného
                            rozpisu (vytvoří chybějící tabulky, aktualizuje hlavičky — adresu, čas, telefon — ručně vyplněné
                            sloupce nepřepisuje). Zároveň skryje listy starší než 14 dní zpět a dál než 30 dní dopředu.
                        </Alert>
                        <Typography variant='subtitle1' fontWeight='bold'>
                            Kdy použít
                        </Typography>
                        <Box component='ul' sx={{ m: 0, pl: 3, typography: 'body2' }}>
                            <li>Když v rozpisu chybí tabulka pro akci, která tam má být.</li>
                            <li>Po hromadných úpravách adres, časů nebo telefonů, abyste nemuseli každou akci otevírat zvlášť.</li>
                            <li>Po prvním nasazení této integrace.</li>
                        </Box>
                    </Stack>
                )}
                {phase === 'loading' && (
                    <Stack spacing={2} alignItems='center' py={4}>
                        <CircularProgress />
                        <Typography>Aktualizuji rozpis…</Typography>
                    </Stack>
                )}
                {phase === 'done' && response && (
                    <Stack spacing={2}>
                        {response.alreadyRunning && (
                            <Alert severity='warning'>Aktualizace už běží. Zkuste to prosím za chvíli.</Alert>
                        )}
                        {response.spreadsheetNotConfigured && (
                            <Alert severity='error'>Cílový rozpis není nakonfigurován. Kontaktujte podporu.</Alert>
                        )}
                        {!response.alreadyRunning && !response.spreadsheetNotConfigured && (
                            <>
                                <Typography variant='subtitle1' fontWeight='bold'>
                                    Souhrn
                                </Typography>
                                <Stack direction='row' spacing={2} flexWrap='wrap'>
                                    <Chip label={`Vytvořeno: ${response.createdCount}`} color='success' />
                                    <Chip label={`Aktualizováno: ${response.updatedCount}`} color='primary' />
                                    <Chip label={`Přesunuto: ${response.movedCount}`} color='warning' />
                                    <Chip label={`Přeskočeno: ${response.skippedCount}`} />
                                    <Chip
                                        label={`Chyba: ${response.failedCount}`}
                                        color={response.failedCount > 0 ? 'error' : 'default'}
                                    />
                                </Stack>
                                {failedResults.length > 0 && (
                                    <>
                                        <Typography variant='subtitle1' fontWeight='bold'>
                                            Chyby
                                        </Typography>
                                        <Stack spacing={1}>
                                            {failedResults.map((r) => (
                                                <EventResultRow key={r.eventId} result={r} />
                                            ))}
                                        </Stack>
                                    </>
                                )}
                            </>
                        )}
                    </Stack>
                )}
            </DialogContent>
            <DialogActions>
                {phase === 'idle' && (
                    <>
                        <Button onClick={handleClose}>Zrušit</Button>
                        <Button onClick={handleRun} variant='contained' autoFocus>
                            Spustit aktualizaci
                        </Button>
                    </>
                )}
                {phase === 'loading' && <Button disabled>Zrušit</Button>}
                {phase === 'done' && (
                    <Button onClick={handleClose} variant='contained'>
                        Zavřít
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    )
}

const EventResultRow: React.FC<{ result: ApprovedEventsSheetSyncEventResultDto }> = ({ result }) => {
    const status = renderOutcomeChip(result.outcome)
    return (
        <Paper variant='outlined' sx={{ p: 2, borderLeft: 4, borderLeftColor: status.color }}>
            <Stack direction='row' justifyContent='space-between' alignItems='baseline'>
                <Typography fontWeight='medium'>{result.eventName ?? '—'}</Typography>
                <Typography variant='caption' color='text.secondary'>
                    {formatDate(result.startsAt)}
                </Typography>
            </Stack>
            {result.errorMessage && (
                <Typography variant='body2' sx={{ color: status.color }}>
                    {result.errorMessage}
                </Typography>
            )}
        </Paper>
    )
}

export default ForceApprovedEventsSheetSyncDialog
